import { Link } from 'react-router-dom';
import CategoryCoverflow from './CategoryCoverflow';
import type { CategoryGroupTile } from './galleryMedia';

/**
 * ~300px on phones, ~360px on tablets, ~420px on desktop — min-h-* breakpoint classes, the same
 * way the hero Carousel (see Carousel.tsx: `min-h-screen lg:min-h-[40rem]`) sizes itself, rather
 * than a JS clamp() string. Sized a bit taller than the active photo itself (see
 * BASE_IMAGE_WIDTH/BASE_ASPECT_RATIO in CategoryCoverflow) so it has breathing room rather than
 * fitting it edge to edge.
 */
const CAROUSEL_HEIGHT_CLASSES = 'min-h-[300px] md:min-h-[360px] lg:min-h-[420px]';

interface CategoryCarouselSectionProps {
  group: CategoryGroupTile;
  className?: string;
}

/**
 * A category with more than one photo gets its own full-width featured strip — an automatic 3D
 * coverflow through its photos, its name (and an optional short description, see
 * sanity/schemaTypes/galleryCategory.ts) overlaid at the bottom-left — instead of a masonry tile.
 * Deliberately rendered above/outside the masonry grid (see Gallery.tsx): a wide, fixed-height
 * "featured section" doesn't belong inside a grid of tall portrait tiles, and mixing the two
 * would force one layout to compromise for the other.
 */
const CategoryCarouselSection: React.FC<CategoryCarouselSectionProps> = ({ group, className = '' }) => (
  <Link
    to={`/gallery/${group.slug}`}
    aria-label={`View all ${group.count} photos in ${group.title}`}
    className={`relative block w-full overflow-hidden rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-custom-hov ${CAROUSEL_HEIGHT_CLASSES} ${className}`}
  >
    {/* Sits on its own small backdrop rather than relying on the carousel behind it: which
        photo happens to be there — light, dark, busy — shouldn't decide whether this stays
        readable. The description is optional (most categories won't have one) and sits right
        beside the title rather than below it, so it reads as a subtitle, not a second caption. */}
    <div className="absolute bottom-3 left-3 z-40 flex max-w-[calc(100%-1.5rem)] flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-md bg-black/45 px-2.5 py-1.5">
      <span className="text-sm font-semibold text-white md:text-base">{group.title}</span>
      {group.description && (
        <span className="truncate text-xs text-white/75 md:text-sm">{group.description}</span>
      )}
    </div>
    <CategoryCoverflow images={group.images} title={group.title} />
  </Link>
);

export default CategoryCarouselSection;
